import {useMemo} from 'react';

import {platformControls} from '../data/controls';
import {
    ControlReadiness,
    ControlState,
    PlatformControl,
} from '../types';
import {
    useSetControlStateMutation,
    useWiredControlsQuery,
} from './usePlatformControlsQuery';

/**
 * FNP-891 — state for the Platform Controls screen.
 *
 * Merges the static registry (which lists EVERY control, including the ones not
 * yet wired, so the remaining work stays visible) with live positions read from
 * console-api for the wired ones.
 *
 * A control's position is only ever ON/OFF when the API actually said so. While
 * loading, on error, or for a not-yet-wired control it stays UNKNOWN — rendering
 * a guessed position for a platform-wide switch is worse than admitting we do not
 * know.
 */
export const usePlatformControls = () => {

    const wiredIds = useMemo(
        () => platformControls
            .filter((control) => control.readiness === ControlReadiness.OPERATIONAL)
            .map((control) => control.id),
        [],
    );

    const results = useWiredControlsQuery(wiredIds);
    const mutation = useSetControlStateMutation();

    const controls = useMemo<PlatformControl[]>(() => {

        const liveById = new Map(
            wiredIds.map((id, index) => [id, results[index]]),
        );

        return platformControls.map((control) => {

            const live = liveById.get(control.id);

            if (!live) {
                return control;
            }

            const record = live.data;

            return {
                ...control,
                state: record?.state ?? ControlState.UNKNOWN,
                lastChangedBy: record?.lastChangedBy ?? undefined,
                lastChangedAt: record?.lastChangedAt ?? undefined,
                flipLatencySeconds: record?.flipLatencySeconds ?? control.flipLatencySeconds,
            };
        });

    }, [results, wiredIds]);

    const loading = results.some((result) => result.isLoading);
    const readError = results.some((result) => result.isError);

    const toggleControl = async (id: string, next: ControlState) => {

        const control = platformControls.find((item) => item.id === id);

        if (!control || control.readiness !== ControlReadiness.OPERATIONAL) {
            return {
                applied: false,
                reason: 'This control cannot be operated yet.',
            };
        }

        if (next === ControlState.UNKNOWN) {
            return {
                applied: false,
                reason: 'A control can only be turned on or off.',
            };
        }

        try {
            await mutation.mutateAsync({
                                           id,
                                           state: next,
                                       });
            return {applied: true};
        } catch (error) {
            // Surface the real reason — an authorization failure here means the
            // caller is not in the admin group, which the operator needs told
            // plainly rather than as a silent no-op.
            return {
                applied: false,
                reason: error instanceof Error
                        ? error.message
                        : 'Could not change the control.',
            };
        }
    };

    return {
        controls,
        loading,
        readError,
        pending: mutation.isPending ? [mutation.variables?.id ?? ''] : [],
        toggleControl,
    };
};
